// @ts-strict-ignore
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, switchMap, tap } from 'rxjs/operators';

import { fetchBackendVersion } from '../store/common/common.actions';
import { selectCommonBackendDataVersion } from '../store/common/common.selectors';
import { selectCountryAndLanguageAndSubprocess } from '../store/runtime-variables/runtime-variables.selectors';
import { filterTruthy } from '../utils/functions/rx-filters';

/**
 * Checks for occurrence of '//<whatever>'
 */
const MATCH_NO_DATAVERSION = /\/\/\b/g;

/**
 * Checks for a protocol like 'https://'
 */
const MATCH_PROTOCOL = /^.*:\/\//i;

/**
 * Checks if the backend data version is available and if not
 * triggers a fetch, waits for the result and insert the version into the URL.
 *
 * false URL e.g. looks like this: 'https://configurator.mercedes-benz.com/integration/cc-backend/api/v3/de_DE/CCci//vehicleClasses/SL-KLASSE/vehicleBodies'
 * or this: '../api/v3/de_DE/CCci//vehicleClasses/SL-KLASSE/vehicleBodies'
 * meaning we have '//whatever' somewhere in the path (excluding 'https://')
 */
@Injectable()
export class BackendVersionInterceptor implements HttpInterceptor {
  private isVersionRequestInProgress = false;

  public constructor(public readonly store: Store) {}

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    /**
     * Insert version into false url.
     * E.g. converts from
     * https://configurator.mercedes-benz.com/integration/cc-backend/api/v3/de_DE/CCci//vehicleClasses/SL-KLASSE/vehicleBodies
     * to
     * https://configurator.mercedes-benz.com/integration/cc-backend/api/v3/de_DE/CCci/5ebcfbff/vehicleClasses/SL-KLASSE/vehicleBodies
     *
     * or
     * ../api/v3/de_DE/CCci//vehicleClasses/SL-KLASSE/vehicleBodies
     * to
     * ../api/v3/de_DE/CCci/5ebcfbff/vehicleClasses/SL-KLASSE/vehicleBodies
     *
     */
    const insertVersion = (url: string, version: string): string => {
      const isAbsolutePath = MATCH_PROTOCOL.test(url);
      if (isAbsolutePath) {
        const split = url.split('//');
        return `${split.shift()}//${split.join('//').replace('//', `/${version}/`)}`;
      }
      // relative path (no protocol like 'https://' but starting with '../')
      return url.replace('//', `/${version}/`);
    };

    /**
     * @returns cloned request with inserted version
     */
    const buildClonedRequestContainingVersion = (req: HttpRequest<unknown>, version: string): HttpRequest<unknown> =>
      req.clone({
        url: insertVersion(request.url, version)
      });

    const isVersionRequest = (req: HttpRequest<unknown>): boolean => req.url.includes('version');

    if (isVersionRequest(request)) {
      return next.handle(request);
    }

    if (this.isVersionRequestInProgress) {
      return this.store.select(selectCommonBackendDataVersion).pipe(
        filterTruthy(),
        first(),
        tap(() => (this.isVersionRequestInProgress = false)),
        switchMap(version => next.handle(buildClonedRequestContainingVersion(request, version)))
      );
    }

    const urlWithoutProtocol = request.url.replace(MATCH_PROTOCOL, '');
    const isMissingDataVersion = MATCH_NO_DATAVERSION.test(urlWithoutProtocol);

    if (isMissingDataVersion) {
      this.isVersionRequestInProgress = true;

      return this.store.select(selectCountryAndLanguageAndSubprocess).pipe(
        filterTruthy(),
        first(),
        tap(({ country, language, subprocess }) => {
          this.store.dispatch(
            fetchBackendVersion({
              market: `${language}_${country}`,
              subProcess: subprocess
            })
          );
        }),
        switchMap(() => this.store.select(selectCommonBackendDataVersion).pipe(filterTruthy(), first())),
        switchMap(version => next.handle(buildClonedRequestContainingVersion(request, version)))
      );
    }
    return next.handle(request);
  }
}
